// ─── CHAD V3 Design Tokens ───────────────────────────────────────────────────
// Single source of truth for all visual design decisions.
// Import from components that need programmatic access to colors.
// CSS variables defined in App.css mirror these values.
// ─────────────────────────────────────────────────────────────────────────────

export const THEME = {
  fonts: {
    body: "'EB Garamond', serif",
    mono: "'IBM Plex Mono', monospace",
    logo: "'Playfair Display', serif",
  },

  light: {
    topbar:       "#1e1e1c",
    topbarText:   "#ede9e0",
    topbarMuted:  "#7a776e",
    topbarBorder: "#2a2a28",
    topbarSep:    "#3a3836",

    body:         "#faf8f3",
    sidebar:      "#f0ede6",
    card:         "#f0ede5",
    inputBar:     "#ece9e1",
    input:        "#faf8f3",

    border:       "#d4cfc4",
    borderHover:  "#b0aca4",

    textPrimary:  "#1a1a18",
    textBody:     "#2a2a24",
    textMuted:    "#6a6660",
    textFaint:    "#9a968a",
    textPlaceholder: "#9a968a",

    accent:       "#d4a04a",
    accentDim:    "rgba(170,125,40,.08)",
    accentBorder: "rgba(170,125,40,.2)",

    red:          "#b8453a",
    green:        "#3a7a56",
    amber:        "#9a7020",

    msgChad:      "#f0ede5",
    msgChadBorder:"#d4cfc4",
    msgUser:      "rgba(170,125,40,.06)",
    msgUserBorder:"rgba(170,125,40,.18)",

    kpiRed:       "#b8453a",
    kpiGreen:     "#3a7a56",
    kpiAmber:     "#9a7020",
  },

  dark: {
    topbar:       "#161615",
    topbarText:   "#ede9e0",
    topbarMuted:  "#7a776e",
    topbarBorder: "#2a2a28",
    topbarSep:    "#3a3836",

    body:         "#1e1e1c",
    sidebar:      "#1a1a18",
    card:         "#252522",
    inputBar:     "#1a1a18",
    input:        "#252522",

    border:       "#2a2a28",
    borderHover:  "#3a3836",

    textPrimary:  "#ede9e0",
    textBody:     "#e0dcd4",
    textMuted:    "#9a968a",
    textFaint:    "#5a5854",
    textPlaceholder: "#5a5854",

    accent:       "#d4a04a",
    accentDim:    "rgba(212,160,74,.12)",
    accentBorder: "rgba(212,160,74,.35)",

    red:          "#e88070",
    green:        "#7ec8a4",
    amber:        "#d4a04a",

    msgChad:      "#252522",
    msgChadBorder:"#2a2a28",
    msgUser:      "rgba(212,160,74,.06)",
    msgUserBorder:"rgba(212,160,74,.15)",

    kpiRed:       "#e88070",
    kpiGreen:     "#7ec8a4",
    kpiAmber:     "#d4a04a",
  },

  animation: {
    glide:    "cubic-bezier(.25,.8,.25,1)",
    duration: "0.4s",
    stagger:  80,  // ms between staggered elements
  },

  layout: {
    topbarHeight: 52,
    railWidth:    56,
    sidebarWidth: 224,
    chatPadding:  28,
    chatMaxWidth: 680,
    borderRadius: 10,
    borderRadiusLg: 12,
    inputRadius:  24,
  },

  // User avatar colors — each user gets a unique accent
  userColors: {
    default: { bg: "rgba(170,125,40,.08)", border: "rgba(170,125,40,.4)", text: "#7a5810" },
    blue:    { bg: "rgba(126,160,200,.08)", border: "rgba(126,160,200,.4)", text: "#4a7098" },
    green:   { bg: "rgba(58,122,86,.08)",   border: "rgba(58,122,86,.4)",   text: "#3a7a56" },
    coral:   { bg: "rgba(184,69,58,.08)",   border: "rgba(184,69,58,.35)",  text: "#984038" },
  },
};

// Role configuration — what each role sees and how CHAD responds
export const MODE_CONFIG = {
  exec: {
    label: "Executive",
    short: "Exec",
    chips: [
      "Project health summary",
      "Total pending CO exposure",
      "Critical schedule risks",
      "Contract value overview",
    ],
    placeholder: "Ask about portfolio performance...",
    systemInstruction: [
      "USER MODE: EXECUTIVE.",
      "Respond as if briefing a senior executive. Be concise — 2-3 sentences max.",
      "Focus on KPIs: cost exposure, schedule risk, contract value, critical delays.",
      "No jargon. Always include a dollar figure or percentage when possible.",
      "followUpSuggestions should be high-level strategic questions only.",
    ].join(" "),
  },
  pm: {
    label: "PM / Eng",
    short: "PM",
    chips: [
      "Daily briefing",
      "Open RFIs with impact",
      "Pending submittals",
      "Change orders by status",
      "Critical path activities",
      "Contract value summary",
    ],
    placeholder: "Ask about {project}...",
    systemInstruction: "",
  },
  field: {
    label: "Field",
    short: "Field",
    chips: [
      "Today's pour schedule",
      "Submittal status",
      "Open RFIs for my area",
      "Material delivery ETA",
    ],
    placeholder: "Ask a quick question...",
    systemInstruction: [
      "USER MODE: FIELD PROFESSIONAL.",
      "Use plain, simple English. No acronyms unless the user used them first.",
      "Answer in 1-2 sentences only. No tables unless comparing multiple items.",
      "Search only the single most relevant dataset.",
      "Field personnel see operational data only — NEVER show unit_cost, bid_total, or dollar amounts.",
      "The insight field must be one short plain-English sentence.",
    ].join(" "),
  },
};

// Demo accounts for the login screen
export const DEMO_ACCOUNTS = {
  "hemanth@ferrocrete.com": { name: "Hemanth", initials: "HM", roles: ["exec", "pm", "field"], color: "default" },
  "raz@ferrocrete.com":     { name: "Raz",     initials: "RZ", roles: ["pm", "field"],         color: "blue" },
  "shant@ferrocrete.com":   { name: "Shant",   initials: "SH", roles: ["field", "pm"],         color: "green" },
};

export const DEMO_PASSWORD = "demo1234";
